// src/components/sections/Hero.jsx
// Full-height hero with video background and typing effect

import React, { useState, useEffect } from 'react';
import useTypingEffect from '../../hooks/useTypingEffect';

const Hero = () => {
  const [loaded, setLoaded] = useState(false);

  const typingText = useTypingEffect(
    ['intelligent', 'connected', 'compliant', 'automated', 'reliable'],
    100, 60, 2000
  );

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      className="min-h-screen flex items-center pt-24 pb-12 md:pt-20 md:pb-16 relative overflow-hidden"
    >
      {/* Video background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
      >
        <source src="/hero-new.mp4" type="video/mp4" />
      </video>

      {/* Dark overlay for text readability */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.6) 100%)',
          zIndex: 1,
        }}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className={`transition-all duration-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-medium bg-white/10 backdrop-blur-sm border border-white/15 text-teal-300">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              Telemetry Software Company
            </div>

            {/* Heading with typing effect */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold tracking-tight mb-6 leading-tight text-white">
              Powering
              <br className="sm:hidden" />
              <span className="block sm:inline min-h-[1.2em] sm:min-h-0">
                {' '}
                <span className="text-teal-400">{typingText}</span>
                <span className="inline-block w-0.5 h-[0.8em] ml-1 align-middle animate-pulse bg-teal-400" />
              </span>
              <br />
              fleet technology
            </h1>

            <p className="text-lg md:text-xl mb-10 text-white/70 leading-relaxed max-w-2xl mx-auto">
              GPS Tech builds telemetry platforms for businesses worldwide.
              Mileage tracking, SARS-compliant logbooks, and enterprise fleet management
              — founded in South Africa, built for the world.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => scrollTo('products')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-all shadow-gt-teal text-base"
              >
                Our Products
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <a
                href="mailto:hello@gpstech.co.za"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl border border-white/25 text-white font-medium hover:bg-white/10 backdrop-blur-sm transition-all text-base"
              >
                Contact Us
              </a>
            </div>
          </div>

          {/* Hex row decoration */}
          <div
            className={`transition-all duration-700 delay-300 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <div className="flex justify-center gap-3 mt-4 opacity-20">
              {[...Array(7)].map((_, i) => (
                <svg key={i} viewBox="0 0 100 100" className="w-10 h-10">
                  <polygon
                    points="50,2 95,25 95,75 50,98 5,75 5,25"
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.3)"
                    strokeWidth="2"
                  />
                </svg>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 z-20">
        <span className="text-xs text-white/40">Scroll to explore</span>
        <svg className="w-5 h-5 animate-bounce text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;